/**
 * useTutorLiveChromeObstruction (web) — la caméra du masteur est-elle masquée ? Port de
 * `common/hooks/livekit/useTutorCameraObstructionDetection.web.js`, à l'identique : une image toutes
 * les 3 s, trois sombres d'affilée (~9 s) la disent masquée, trois claires la libèrent
 * (`cameraFrameAnalysis`, la mesure du test d'avant séance) ; `cam_obstructed_start` et
 * `cam_obstructed_end` au journal du live (`LiveEventBuffer`, `tutorCameraMaskedDuration`).
 *
 * `closeOut` écrit la fin d'une obstruction en cours : l'écran l'appelle avant de quitter ou de
 * clôturer, parce que le journal détruit ensuite perd tout relevé tardif (`LiveModeFooter.js:362`, `:372`).
 * La croix de l'alerte (`dismiss`) ne la montre plus jusqu'à la prochaine obstruction ; le « ! » reste.
 */
import { useCallback, useEffect, useRef, useState } from "react"

import LiveEventBuffer from "../../../../../../data/services/LiveEventBuffer"
import {
    CANVAS_HEIGHT,
    CANVAS_WIDTH,
    CONSECUTIVE_SAMPLES_TO_CONFIRM,
    isFrameObstructed,
    luminanceStats,
} from "../../../../../../engine/precall/cameraFrameAnalysis"
import { type CameraObstruction, type CameraObstructionArgs } from "./useTutorLiveChromeObstruction"

const SAMPLE_INTERVAL_MS = 3000

export function useTutorLiveChromeObstruction(args: CameraObstructionArgs): CameraObstruction {
    const { enabled, cameraOn, localParticipant } = args
    const [obstructed, setObstructed] = useState(false)
    const [alertVisible, setAlertVisible] = useState(false)
    const obstructedRef = useRef(false)
    const dismissedRef = useRef(false)
    const darkRef = useRef(0)
    const clearRef = useRef(0)
    const videoRef = useRef<HTMLVideoElement | null>(null)
    const ctxRef = useRef<CanvasRenderingContext2D | null>(null)
    const lumasRef = useRef<Float32Array | null>(null)
    const participantRef = useRef(localParticipant)
    participantRef.current = localParticipant

    const closeOut = useCallback(() => {
        if (obstructedRef.current) {
            LiveEventBuffer.getInstance().record("cam_obstructed_end", {})
            obstructedRef.current = false
            setObstructed(false)
        }
        setAlertVisible(false)
        dismissedRef.current = false
        darkRef.current = 0
        clearRef.current = 0
    }, [])

    const sample = useCallback(() => {
        const video = videoRef.current
        const ctx = ctxRef.current
        const lumas = lumasRef.current
        if (video == null || ctx == null || lumas == null) return
        // La piste se relit à chaque relevé : LiveKit la remplace à une reprise ou un changement d'appareil.
        const track = participantRef.current?.getTrackPublication?.("camera" as never)?.track?.mediaStreamTrack as
            MediaStreamTrack | undefined
        const attached = (video.srcObject as MediaStream | null)?.getVideoTracks?.()[0]
        if (track == null || track.readyState === "ended") {
            if (video.srcObject != null) video.srcObject = null
            closeOut()
            return
        }
        if (attached !== track) {
            video.srcObject = new MediaStream([track])
            darkRef.current = 0
            clearRef.current = 0
            video.play().catch(() => {})
            return
        }
        if (video.readyState < 2) {
            video.play().catch(() => {})
            return
        }
        let pixels: Uint8ClampedArray
        try {
            ctx.drawImage(video, 0, 0, CANVAS_WIDTH, CANVAS_HEIGHT)
            pixels = ctx.getImageData(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT).data
        } catch {
            return
        }
        if (isFrameObstructed(luminanceStats(pixels, lumas))) {
            darkRef.current += 1
            clearRef.current = 0
            if (!obstructedRef.current && darkRef.current >= CONSECUTIVE_SAMPLES_TO_CONFIRM) {
                obstructedRef.current = true
                setObstructed(true)
                if (!dismissedRef.current) setAlertVisible(true)
                LiveEventBuffer.getInstance().record("cam_obstructed_start", {})
            }
        } else {
            clearRef.current += 1
            darkRef.current = 0
            if (obstructedRef.current && clearRef.current >= CONSECUTIVE_SAMPLES_TO_CONFIRM) closeOut()
        }
    }, [closeOut])

    useEffect(() => {
        if (!enabled || !cameraOn) {
            closeOut()
            return undefined
        }
        if (videoRef.current == null) {
            const video = document.createElement("video")
            video.muted = true
            video.autoplay = true
            video.playsInline = true
            Object.assign(video.style, {
                position: "fixed",
                top: "-9999px",
                left: "-9999px",
                width: "1px",
                height: "1px",
                pointerEvents: "none",
            })
            document.body.appendChild(video)
            videoRef.current = video
        }
        if (ctxRef.current == null) {
            const canvas = document.createElement("canvas")
            canvas.width = CANVAS_WIDTH
            canvas.height = CANVAS_HEIGHT
            ctxRef.current = canvas.getContext("2d", { willReadFrequently: true })
            lumasRef.current = new Float32Array(CANVAS_WIDTH * CANVAS_HEIGHT)
        }
        sample()
        const interval = setInterval(sample, SAMPLE_INTERVAL_MS)
        return () => {
            clearInterval(interval)
            if (videoRef.current != null) videoRef.current.srcObject = null
        }
    }, [enabled, cameraOn, sample, closeOut])

    useEffect(
        () => () => {
            if (obstructedRef.current) LiveEventBuffer.getInstance().record("cam_obstructed_end", {})
            const video = videoRef.current
            if (video != null) {
                video.srcObject = null
                video.parentNode?.removeChild(video)
            }
            videoRef.current = null
            ctxRef.current = null
            lumasRef.current = null
        },
        []
    )

    return {
        obstructed,
        alertVisible,
        dismiss: () => {
            setAlertVisible(false)
            dismissedRef.current = true
        },
        closeOut,
    }
}
